import {
	createFileRoute,
	Link,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import prettyBytes from "pretty-bytes";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/utils/cn";
import type { cursosBatchImportMutation } from "./__generated__/cursosBatchImportMutation.graphql";
import type { cursosBatchQuery } from "./__generated__/cursosBatchQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/aulas/batch",
)({
	component: RouteComponent,
});

type ParsedLesson = {
	name: string;
	description: string;
	videoUrl: string;
};

type ParsedSection = {
	name: string;
	isNew: boolean;
	existingSectionId?: string;
	lessons: ParsedLesson[];
};

type Step = "upload" | "review";

function parseXlsxFile(
	file: File,
	existingSections: { id: string; name: string }[],
): Promise<ParsedSection[]> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = e.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const rows: string[][] = XLSX.utils.sheet_to_json(worksheet, {
					header: 1,
				});

				const sections: ParsedSection[] = [];
				let currentSection: ParsedSection | null = null;

				// Skip header row if it looks like headers
				const firstCell = rows[0]?.[0]?.toString().toLowerCase() || "";
				const startRow =
					firstCell === "módulo" ||
					firstCell === "modulo" ||
					firstCell === "module" ||
					firstCell === "seção" ||
					firstCell === "secao" ||
					firstCell === "section"
						? 1
						: 0;

				for (let i = startRow; i < rows.length; i++) {
					const row = rows[i];
					if (!row || row.length === 0) continue;

					const sectionName = row[0]?.toString().trim();
					const lessonName = row[1]?.toString().trim();
					const description = row[2]?.toString().trim() || "";
					const videoUrl = row[3]?.toString().trim() || "";

					if (!sectionName && !lessonName) continue;

					// If we have a section name, start or switch sections
					if (sectionName) {
						// Check if section already exists in the list we're building
						const existingParsedSection = sections.find(
							(s) => s.name.toLowerCase() === sectionName.toLowerCase(),
						);

						if (existingParsedSection) {
							currentSection = existingParsedSection;
						} else {
							// Check if section exists in the course
							const existingCourseSection = existingSections.find(
								(s) => s.name.toLowerCase() === sectionName.toLowerCase(),
							);

							currentSection = {
								name: sectionName,
								isNew: !existingCourseSection,
								existingSectionId: existingCourseSection?.id,
								lessons: [],
							};
							sections.push(currentSection);
						}
					}

					// Add lesson if we have a lesson name and a current section
					if (lessonName && currentSection) {
						currentSection.lessons.push({
							name: lessonName,
							description,
							videoUrl,
						});
					}
				}

				resolve(sections);
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
		reader.readAsBinaryString(file);
	});
}

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: courseSlug } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });

	const [step, setStep] = useState<Step>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [sections, setSections] = useState<ParsedSection[]>([]);
	const [parseError, setParseError] = useState<string | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	const data = useLazyLoadQuery<cursosBatchQuery>(
		graphql`
			query cursosBatchQuery($clubSlug: String!, $courseSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						sections(status: ALL) {
							id
							name
						}
					}
				}
			}
		`,
		{ clubSlug: club.slug, courseSlug },
	);

	const [importCourseData] = useMutation<cursosBatchImportMutation>(
		graphql`
			mutation cursosBatchImportMutation($payload: BatchImportCourseInput!) {
				importCourseData(payload: $payload) {
					id
					sections(status: ALL) {
						id
						name
						classes(status: ALL) {
							id
							name
							slug
						}
					}
				}
			}
		`,
	);

	const existingSections = data.club.course.sections.map((s) => ({
		id: s.id,
		name: s.name,
	}));

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			const uploadedFile = acceptedFiles[0];
			if (!uploadedFile) return;

			setFile(uploadedFile);
			setParseError(null);

			try {
				const parsedSections = await parseXlsxFile(
					uploadedFile,
					existingSections,
				);

				if (parsedSections.length === 0) {
					setParseError(
						"Nenhum dado encontrado no arquivo. Verifique se o formato está correto.",
					);
					return;
				}

				const totalLessons = parsedSections.reduce(
					(acc, s) => acc + s.lessons.length,
					0,
				);
				if (totalLessons === 0) {
					setParseError(
						"Nenhuma aula encontrada no arquivo. Verifique se o formato está correto.",
					);
					return;
				}

				setSections(parsedSections);
				setStep("review");
			} catch {
				setParseError(
					"Erro ao processar arquivo. Verifique se é um arquivo .xlsx válido.",
				);
			}
		},
		[existingSections],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
		},
		maxFiles: 1,
	});

	const totalLessons = sections.reduce((acc, s) => acc + s.lessons.length, 0);
	const newSectionsCount = sections.filter((s) => s.isNew).length;

	const handleCreate = () => {
		setIsCreating(true);

		// Transform sections into the batch import format
		const batchCourses = sections.map((section) => ({
			module: section.name,
			classes: section.lessons.map((lesson, index) => ({
				id: `temp-${Date.now()}-${index}`,
				title: lesson.name,
				section: section.name,
				description: lesson.description,
				externalVideoUrl: lesson.videoUrl || null,
			})),
		}));

		importCourseData({
			variables: {
				payload: {
					course: data.club.course.id,
					batchCourses,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success(
					`${totalLessons} aula${totalLessons !== 1 ? "s" : ""} criada${totalLessons !== 1 ? "s" : ""} com sucesso!`,
				);
				navigate({
					to: "/c/$club/area-de-membros/cursos/$slug",
					params: { club: club.slug, slug: courseSlug },
				});
				setIsCreating(false);
			},
			onError: (error) => {
				console.error("Error importing course data:", error);
				toast.error("Erro ao criar aulas. Tente novamente.");
				setIsCreating(false);
			},
		});
	};

	const resetFlow = () => {
		setFile(null);
		setSections([]);
		setParseError(null);
		setStep("upload");
	};

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[800px] flex flex-col gap-6 border rounded-md">
				<div className="flex items-center justify-between">
					<Heading>Subir Aulas em Batch</Heading>
					<Button variant="ghost" size="sm" asChild>
						<Link
							to="/c/$club/area-de-membros/cursos/$slug"
							params={{ club: club.slug, slug: courseSlug }}
						>
							<Icon name="X" className="size-4" />
						</Link>
					</Button>
				</div>

				{/* Progress indicator */}
				<div className="flex items-center gap-2">
					<div
						className={cn(
							"h-1 flex-1 rounded-md transition-colors",
							"bg-primary",
						)}
					/>
					<div
						className={cn(
							"h-1 flex-1 rounded-md transition-colors",
							step === "review" ? "bg-primary" : "bg-muted",
						)}
					/>
				</div>

				{/* Step 1: Upload */}
				{step === "upload" && (
					<div className="flex flex-col gap-6">
						<div
							{...getRootProps()}
							className={cn(
								"border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
								isDragActive
									? "border-primary bg-primary/5"
									: "border-muted-foreground/25 hover:border-muted-foreground/50",
							)}
						>
							<input {...getInputProps()} />
							<div className="flex flex-col items-center gap-3">
								<div className="size-12 rounded-full bg-muted flex items-center justify-center">
									<Icon
										name="Upload"
										className="size-6 text-muted-foreground"
									/>
								</div>
								{isDragActive ? (
									<p className="text-foreground">Solte o arquivo aqui...</p>
								) : (
									<>
										<p className="text-foreground font-medium">
											Aulas e Módulos
										</p>
										<p className="text-sm text-muted-foreground">
											Suba um arquivo .xlsx com módulos, aulas, descrição e URL
											do vídeo, seguindo{" "}
											<a
												href="https://d36oqrf5rixt5a.cloudfront.net/templates/aulas.xlsx"
												download
												className="text-primary underline underline-offset-2"
												onClick={(e) => e.stopPropagation()}
											>
												esse template
											</a>
											.
										</p>
									</>
								)}
							</div>
						</div>

						{file && (
							<Card className="flex items-center gap-3 p-3">
								<div className="size-10 rounded bg-green-700 flex items-center justify-center text-white text-xs font-semibold uppercase">
									xlsx
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{file.name}</p>
									<p className="text-xs text-muted-foreground">
										{prettyBytes(file.size)}
									</p>
								</div>
								<Button
									variant="ghost"
									size="icon-sm"
									onClick={() => {
										setFile(null);
										setParseError(null);
									}}
								>
									<Icon name="X" className="size-4" />
								</Button>
							</Card>
						)}

						{parseError && (
							<Alert variant="destructive">
								<Icon name="CircleAlert" className="size-4" />
								<AlertTitle>Erro ao processar arquivo</AlertTitle>
								<AlertDescription>{parseError}</AlertDescription>
							</Alert>
						)}
					</div>
				)}

				{/* Step 2: Review */}
				{step === "review" && (
					<div className="flex flex-col gap-6">
						<div className="flex items-center justify-between">
							<p className="text-sm text-muted-foreground">
								{sections.length} módulo{sections.length !== 1 ? "s" : ""} •{" "}
								{totalLessons} aula{totalLessons !== 1 ? "s" : ""} encontrada
								{totalLessons !== 1 ? "s" : ""}
								{newSectionsCount > 0 && (
									<>
										{" "}
										• {newSectionsCount} módulo
										{newSectionsCount !== 1 ? "s" : ""} novo
										{newSectionsCount !== 1 ? "s" : ""}
									</>
								)}
							</p>
							<Button variant="ghost" size="sm" onClick={resetFlow}>
								<Icon name="RotateCcw" className="size-4" />
								Trocar arquivo
							</Button>
						</div>

						<div className="flex flex-col gap-3 verflow-y-auto">
							{sections.map((section, sectionIndex) => (
								<div
									key={sectionIndex}
									className="border rounded-lg overflow-hidden"
								>
									<div className="flex items-center gap-3 p-3 bg-muted/50">
										<Icon
											name="FolderOpen"
											className="size-4 text-muted-foreground"
										/>
										<span className="font-medium flex-1">{section.name}</span>
										{section.isNew ? (
											<Badge variant="secondary">Novo</Badge>
										) : (
											<Badge variant="outline">Existente</Badge>
										)}
									</div>
									{section.lessons.length > 0 && (
										<div className="divide-y border-t">
											{section.lessons.map((lesson, lessonIndex) => (
												<div
													key={lessonIndex}
													className="flex items-center gap-3 p-3 pl-10 bg-background"
												>
													<Icon
														name={lesson.videoUrl ? "Video" : "FileText"}
														className="size-4 text-muted-foreground shrink-0"
													/>
													<div className="flex-1 min-w-0">
														<span className="text-sm">{lesson.name}</span>
														{lesson.videoUrl && (
															<p className="text-xs text-muted-foreground truncate">
																{lesson.videoUrl}
															</p>
														)}
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							))}
						</div>

						<Separator />

						<div className="flex items-center justify-between">
							<Button
								variant="outline"
								onClick={() => setStep("upload")}
								disabled={isCreating}
							>
								<Icon name="ArrowLeft" className="size-4" />
								Voltar
							</Button>
							<Button onClick={handleCreate} disabled={isCreating}>
								{isCreating ? (
									<>
										<Spinner className="size-4" />
										Criando...
									</>
								) : (
									<>
										<Icon name="Plus" className="size-4" />
										Criar {totalLessons} aula{totalLessons !== 1 ? "s" : ""}
									</>
								)}
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
